import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Inspection } from '../../shared/interfaces';
import { InspectionService } from '../inspection.service';
import { DialogService } from '../../dialog/dialog.service';
import { galleryTokenCreatedAtFromResponse, isActiveGalleryToken } from '../gallery-token';

@Component({
  selector: 'app-inspection-share-gallery-modal',
  templateUrl: './inspection-share-gallery-modal.component.html',
  styleUrls: ['./inspection-share-gallery-modal.component.scss'],
})
export class InspectionShareGalleryModalComponent implements OnInit, OnChanges {
  @ViewChild('modalRef', { static: false }) modalRef!: ModalDirective;
  @Input() inspection!: Inspection;
  @Output() inspectionUpdated = new EventEmitter<Inspection>();

  withMarkupsEnabled = true;
  withoutMarkupsEnabled = true;
  loading = false;
  processingWithMarkups = false;
  processingWithoutMarkups = false;

  constructor(
    private toastr: ToastrService,
    private inspectionService: InspectionService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.syncToggleStates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inspection'] && !this.processingWithMarkups && !this.processingWithoutMarkups) {
      this.syncToggleStates();
    }
  }

  openModal() {
    this.syncToggleStates();
    this.modalRef.show();
  }

  closeModal() {
    this.modalRef.hide();
  }

  // with markups token
  async updateWithMarkupsToken(): Promise<void> {
    // Prevent double-clicks
    if (this.processingWithMarkups) {
      return;
    }

    this.processingWithMarkups = true;

    if (this.withMarkupsEnabled) {
      try {
        this.loading = true;
        const response: any = await this.inspectionService.getWithMarkupsToken(this.inspection.id);
        const token = response?.token ?? '';
        if (!token) {
          this.loading = false;
          this.processingWithMarkups = false;
          this.withMarkupsEnabled = false; // Revert toggle if token generation failed
          return;
        }
        this.inspection.galleryWithMarkupToken = token;
        this.inspection.galleryMarkupTokenCreatedAt = galleryTokenCreatedAtFromResponse(
          response,
          this.inspection.galleryMarkupTokenCreatedAt,
        );
        this.inspectionUpdated.emit(this.inspection);
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.withMarkupsEnabled = false; // Revert toggle on error
        this.toastr.error('Failed to get gallery token');
        console.error('Error trying to get with markups token:', error);
      } finally {
        this.processingWithMarkups = false;
      }
    } else {
      this.modalRef.hide();

      // confirm dialog to delete with markups token
      this.dialogService.confirm().show({
        header: ' ',
        messageHtml: `<div style="margin-top: -8px !important; font-size: 14px !important;">Disabling will immediately deactivate the current shared link! If you enable sharing again, a new link will be
        generated.</div>`,
        confirmed: async () => {
          try {
            await this.inspectionService.deleteWithMarkupsToken(this.inspection.id);
            this.inspection.galleryWithMarkupToken = null;
            this.inspection.galleryMarkupTokenCreatedAt = null;
            this.withMarkupsEnabled = false;
            this.inspectionUpdated.emit(this.inspection);
          } catch (error) {
            this.toastr.error('Failed to delete gallery token');
            console.error('Error trying to delete with markups token:', error);
            this.withMarkupsEnabled = true; // Revert toggle on error
          } finally {
            this.processingWithMarkups = false;
          }
          this.modalRef.show();
        },
        canceled: () => {
          this.withMarkupsEnabled = true; // Revert toggle if canceled
          this.processingWithMarkups = false;
          this.modalRef.show();
        },
      });
    }
  }

  // without markups token
  async updateWithoutMarkupsToken(): Promise<void> {
    // Prevent double-clicks
    if (this.processingWithoutMarkups) {
      return;
    }

    this.processingWithoutMarkups = true;

    if (this.withoutMarkupsEnabled) {
      try {
        this.loading = true;
        const response: any = await this.inspectionService.getWithoutMarkupsToken(this.inspection.id);
        const token = response?.token ?? '';
        if (!token) {
          this.loading = false;
          this.processingWithoutMarkups = false;
          this.withoutMarkupsEnabled = false; // Revert toggle if token generation failed
          return;
        }
        this.inspection.galleryWithoutMarkupToken = token;
        this.inspection.galleryTokenCreatedAt = galleryTokenCreatedAtFromResponse(
          response,
          this.inspection.galleryTokenCreatedAt,
        );
        this.inspectionUpdated.emit(this.inspection);
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.withoutMarkupsEnabled = false; // Revert toggle on error
        this.toastr.error('Failed to get gallery token');
        console.error('Error trying to get without markups token:', error);
      } finally {
        this.processingWithoutMarkups = false;
      }
    } else {
      this.modalRef.hide();

      // confirm dialog to delete without markups token
      this.dialogService.confirm().show({
        header: ' ',
        messageHtml: `<div style="margin-top: -8px !important; font-size: 14px !important;">Disabling will immediately deactivate the current shared link! If you enable sharing again, a new link will be
        generated.</div>`,
        confirmed: async () => {
          try {
            await this.inspectionService.deleteWithoutMarkupsToken(this.inspection.id);
            this.inspection.galleryWithoutMarkupToken = null;
            this.inspection.galleryTokenCreatedAt = null;
            this.withoutMarkupsEnabled = false;
            this.inspectionUpdated.emit(this.inspection);
          } catch (error) {
            this.toastr.error('Failed to delete gallery token');
            console.error('Error trying to delete without markups token:', error);
            this.withoutMarkupsEnabled = true; // Revert toggle on error
          } finally {
            this.processingWithoutMarkups = false;
          }
          this.modalRef.show();
        },
        canceled: () => {
          this.withoutMarkupsEnabled = true; // Revert toggle if canceled
          this.processingWithoutMarkups = false;
          this.modalRef.show();
        },
      });
    }
  }

  getWithMarkupsUrl(): string {
    if (!isActiveGalleryToken(this.inspection?.galleryWithMarkupToken, this.inspection?.galleryMarkupTokenCreatedAt)) {
      return '';
    }
    return `${window.location.origin}/gallery/${this.inspection.galleryWithMarkupToken}`;
  }

  getWithoutMarkupsUrl(): string {
    if (!isActiveGalleryToken(this.inspection?.galleryWithoutMarkupToken, this.inspection?.galleryTokenCreatedAt)) {
      return '';
    }
    return `${window.location.origin}/gallery/${this.inspection.galleryWithoutMarkupToken}`;
  }

  copyToClipboard(url: string, type: 'with' | 'without') {
    if (!url) {
      this.toastr.warning(
        `Gallery link for ${type === 'with' ? 'with markups/flags' : 'without markups/flags'} is not available`,
      );
      return;
    }
    navigator.clipboard
      .writeText(url)
      .then(() => {
        this.toastr.success('Link copied!');
      })
      .catch(() => {
        this.toastr.error('Failed to copy link');
      });
  }

  private syncToggleStates() {
    this.withMarkupsEnabled = isActiveGalleryToken(
      this.inspection?.galleryWithMarkupToken,
      this.inspection?.galleryMarkupTokenCreatedAt,
    );
    this.withoutMarkupsEnabled = isActiveGalleryToken(
      this.inspection?.galleryWithoutMarkupToken,
      this.inspection?.galleryTokenCreatedAt,
    );
  }
}
